<template>
    <div>
        <button
            type="button"
            class="inline-flex items-center rounded border font-semibold"
            :class="buttonClasses"
            @click="open"
        >
            <i class="far fa-comment-alt mr-2"></i>
            {{ $t('Message') }}
        </button>

        <modal
            :name="modalName"
            :adaptive="true"
            :focusTrap="true"
            :width="720"
            :height="640"
            :scrollable="true"
            :styles="{ boxShadow: 'none', background: 'transparent' }"
            @opened="focusInitialField"
        >
            <form class="rounded-lg bg-white px-5 pb-5 pt-4 sm:px-8 sm:pb-8 sm:pt-5 h-full flex flex-col" @submit.prevent="send">
                <div class="flex items-center justify-between mb-6">
                    <div>
                        <h2 class="text-xl font-semibold text-gray-900">
                            {{ $t('Message') }} {{ options.recipient.name }}
                        </h2>
                    </div>
                    <button type="button" class="text-gray-400 hover:text-gray-600 text-2xl" @click="close">
                        <i class="far fa-times-circle"></i>
                    </button>
                </div>

                <div v-if="options.sms_available || options.whatsapp_available" class="flex border-b border-gray-300 mb-5">
                    <button type="button" class="px-4 py-2" :class="tabClass('email')" @click="selectChannel('email')">
                        <i class="far fa-envelope mr-2"></i>Email
                    </button>
                    <button
                        v-if="options.sms_available"
                        type="button"
                        class="px-4 py-2"
                        :class="tabClass('sms')"
                        @click="selectChannel('sms')"
                    >
                        <i class="fas fa-mobile-alt mr-2"></i>Text Message
                    </button>
                    <button v-if="options.whatsapp_available" type="button" class="px-4 py-2" :class="tabClass('whatsapp')" @click="selectChannel('whatsapp')">
                        <i class="fab fa-whatsapp mr-2"></i>WhatsApp
                    </button>
                </div>

                <div class="rounded border border-gray-300 bg-gray-warm-75 px-4 py-3 text-sm text-gray-700 mb-4">
                    {{ recipientLabel }}
                </div>

                <div v-if="channel === 'email'" class="mb-4">
                    <label class="block font-semibold text-sm text-gray-700 mb-2">Subject</label>
                    <input
                        ref="subject"
                        v-model="subject"
                        class="form-input w-full rounded border border-gray-400 px-3 py-2 focus:border-blue-500 focus:ring focus:ring-blue-200"
                        maxlength="255"
                        required
                    />
                </div>

                <div class="mb-5 flex-1 flex flex-col">
                    <label class="block font-semibold text-sm text-gray-700 mb-2">Message</label>
                    <textarea
                        ref="body"
                        v-model="body"
                        class="form-textarea w-full flex-1 resize-none rounded border border-gray-400 px-3 py-2 focus:border-blue-500 focus:ring focus:ring-blue-200"
                        :maxlength="channel === 'whatsapp' ? 1600 : 10000"
                        required
                    ></textarea>
                </div>

                <p v-if="channel === 'whatsapp'" class="text-xs text-gray-500 mb-4">WhatsApp requires the recipient to have messaged our WhatsApp number within the last 24 hours. Maximum 1,600 characters.</p>

                <div v-if="error" class="text-red-600 text-sm mb-4">{{ error }}</div>
                <div class="flex justify-end items-center space-x-3">
                    <button type="button" class="rounded border border-gray-400 px-5 py-2 text-gray-700" @click="close">Cancel</button>
                    <button
                        type="submit"
                        class="rounded bg-blue-600 px-5 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        :disabled="sending || ! canSend"
                    >
                        {{ sending ? 'Sending…' : 'Send' }}
                    </button>
                </div>
            </form>
        </modal>
    </div>
</template>

<script>
export default {
    props: {
        options: { required: true },
        variant: { default: 'default' },
    },

    data() {
        return {
            channel: 'email',
            subject: this.options.subject,
            body: this.options.body,
            sending: false,
            error: null,
        };
    },

    computed: {
        modalName() {
            return `team-message-${this.options.recipient.id}-${this.options.property_id || 'dashboard'}`;
        },
        buttonClasses() {
            if (this.variant === 'ribbon') {
                return 'my-2 border-blue-400 px-2 py-1 text-xs text-blue-400 hover:border-blue-600 hover:bg-blue-600 hover:text-white';
            }

            if (this.variant === 'small') {
                return 'border-blue-500 px-2 py-0.5 text-2xs text-blue-500 hover:bg-blue-500 hover:text-white';
            }

            return 'border-blue-500 px-3 py-2 text-sm text-blue-500 hover:bg-blue-500 hover:text-white';
        },
        recipientLabel() {
            if (this.channel === 'whatsapp') return 'WhatsApp to ' + this.options.recipient.whats_app_number;
            return this.channel === 'email' ? 'Email to ' + this.options.recipient.email : 'Text message to ' + this.options.recipient.phone;
        },
        canSend() {
            const hasBody = (this.body || '').trim().length > 0;
            const hasRequiredSubject = this.channel !== 'email'
                || (this.subject || '').trim().length > 0;

            return hasBody && hasRequiredSubject
                && (this.channel !== 'sms' || !!this.options.sms_available)
                && (this.channel !== 'whatsapp' || (this.options.whatsapp_available && this.body.length <= 1600));
        },
    },

    methods: {
        open() {
            this.subject = this.options.subject;
            this.body = this.options.body;
            this.channel = 'email';
            this.error = null;
            this.$modal.show(this.modalName);
        },
        close() {
            this.$modal.hide(this.modalName);
        },
        tabClass(channel) {
            return this.channel === channel
                ? 'border-b-2 border-blue-600 text-blue-700'
                : 'border-b-2 border-transparent text-gray-500';
        },
        selectChannel(channel) {
            if (channel === 'sms' && !this.options.sms_available) return;
            if (channel === 'whatsapp' && !this.options.whatsapp_available) return;
            this.channel = channel;
            this.error = null;
            this.$nextTick(this.focusInitialField);
        },
        focusInitialField() {
            const input = this.channel === 'email' ? this.$refs.subject : this.$refs.body;
            if (input) {
                input.focus();
            }
        },
        send() {
            if (this.sending || ! this.canSend) {
                return;
            }

            this.sending = true;
            this.error = null;

            axios.post(this.options.send_url, {
                channel: this.channel,
                subject: this.channel === 'email' ? this.subject : null,
                body: this.body,
                property_id: this.options.property_id,
            }).then(() => {
                this.close();
            }).catch(error => {
                this.error = error.response?.data?.message || 'The message could not be sent.';
            }).finally(() => {
                this.sending = false;
            });
        },
    },
};
</script>
